import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { PhotoGrid, thumb } from "../components/PhotoGrid";
import { api } from "../lib/api";

export function RoutesIndex() {
  const [routes, setRoutes] = useState([]);
  useEffect(() => { api.get("/photos/routes").then((r) => setRoutes(r.data)).catch(() => {}); }, []);
  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="routes-index">
      <Seo title="Photography by Route & Region — CaminoSapiens" description="Browse our photography by Camino route and region — the Camino Francés, Portuguese Camino, Coastal Camino, Galicia and more." />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Photography", to: "/photography" }, { label: "Route & Region" }]} />
      <div className="max-w-2xl mb-12">
        <p className="overline mb-4">Photography</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">By Route &amp; Region</h1>
        <p className="text-[#4A4947] mt-6 leading-relaxed">The routes and regions of our long walks — each a gathering of photographs from that path.</p>
      </div>
      {routes.length === 0 && <p className="text-[#4A4947]">No routes tagged yet.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {routes.map((r) => (
          <Link key={r.slug} to={`/photography/routes/${encodeURIComponent(r.slug)}`} data-testid={`route-card-${r.slug}`} className="group block">
            <div className="aspect-[4/3] overflow-hidden border border-[#E4E1DB] bg-[#F0EFEA]">
              {r.cover && <img src={thumb(r.cover, 800)} alt={r.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />}
            </div>
            <div className="pt-3 flex items-baseline justify-between">
              <p className="font-display text-xl">{r.label}</p>
              <p className="text-sm text-[#9a9793]">{r.count}</p>
            </div>
            {r.description && <p className="text-sm text-[#9a9793] mt-1 leading-relaxed">{r.description}</p>}
          </Link>
        ))}
      </div>
    </div>
  );
}

export function RouteGallery() {
  const { slug } = useParams();
  const routeSlug = decodeURIComponent(slug || "");
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get(`/photos/routes/${encodeURIComponent(routeSlug)}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, [routeSlug]);
  const label = detail?.label || routeSlug;
  const hero = detail?.hero;
  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="route-gallery">
      <Seo title={`${label} — Photography — CaminoSapiens`} description={detail?.description || `Photographs from the ${label} — part of our long walks on the Camino de Santiago.`} />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Photography", to: "/photography" }, { label: "Route & Region", to: "/photography/routes" }, { label }]} />
      <div className="max-w-2xl mb-10">
        <p className="overline mb-4">Route &amp; Region</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">{label}</h1>
        {detail?.description && <p className="text-[#4A4947] mt-6 leading-relaxed" data-testid="route-description">{detail.description}</p>}
      </div>
      {hero && (
        <figure className="mb-12" data-testid="route-hero">
          <img src={thumb(hero.image, 1600)} alt={hero.alt || hero.title || label} className="w-full max-h-[70vh] object-cover border border-[#E4E1DB]" />
          {hero.title && <figcaption className="pt-3 font-display text-lg text-[#4A4947]">{hero.title}</figcaption>}
        </figure>
      )}
      {detail?.story && (
        <div className="mb-12 border-y border-[#E4E1DB] py-6 flex flex-wrap items-baseline justify-between gap-3" data-testid="route-story-link">
          <p className="text-[#4A4947]">Read the walk behind these photographs.</p>
          <Link to={`/stories/${detail.story.slug}`} data-testid="route-story-cta" className="inline-flex items-center gap-2 text-sm link-underline text-[#1C1B1A]">
            {detail.story.title} →
          </Link>
        </div>
      )}
      <PhotoGrid filters={{ place: routeSlug }} excludeId={hero?.id} emptyText="No photographs tagged to this route yet." />
    </div>
  );
}
